import React from 'react';
import {
  useChatStore,
  type ContentBlock,
  type ToolUseBlock,
} from '../../store/chatStore';
import { ImageContent } from './ImageContent';
import { ImageUrlContent } from './ImageUrlContent';
import { DocumentContent } from './PDFContent';
import { FileContent } from './FileContent';
import { ToolUseContent } from './ToolUseContent';
import { ReasoningContent } from './ReasoningContent';
import { ErrorContent } from './ErrorContent';
import { TextContent } from './TextContent';
import { FileTextContent } from './FileTextContent';
import { MarkdownWrapper } from './markdown';
import { UniversalDashboardRenderer } from '../../../../dashboard/components/common/UniversalDashboardRenderer';
import { SimpleDashboardConfig } from '../../../../dashboard/components/Dashboard/Dashboard';
import { AskQuestionContent } from './AskQuestionContent';
import { PlanContent } from './PlanContent';
import { UpdatePlanProgressContent } from './UpdatePlanProgressContent';
import { AgentActivityPill } from './AgentActivityPill';
import { HtmlRenderer } from './HtmlRenderer';
import { HtmlFromUrlRenderer } from './HtmlFromUrlRenderer';
import { PdfArtifactCard } from './PdfArtifactCard';
import { useToolResultContent } from './ToolResultDataContext';
import { CloudShellDownloadLinkResult } from './CloudShellPublishResult';
import { PrintResultBlock } from './PrintResultBlock';
import { TerminalCommandBlock } from './TerminalCommandBlock';
import { WriteFilesBlock } from './WriteFilesBlock';
import { ReadFileBlock } from './ReadFileBlock';
import { EditFileBlock } from './EditFileBlock';
import { ListFilesBlock } from './ListFilesBlock';
import { CopyFileBlock } from './CopyFileBlock';
import { DeleteFileBlock } from './DeleteFileBlock';
import { SkillManagementBlock } from './SkillManagementBlock';
import { ShellCredentialBlock } from './ShellCredentialBlock';
import { ToolRendererErrorBoundary } from './ToolRendererErrorBoundary';
import { StreamingGuard } from './StreamingGuard';
import { convertToolCallChunkToToolUse } from './chunkCache';

import { useTranslation } from 'react-i18next';

/** Thin wrapper so we can call the hook at component top level (rules of hooks). */
const RenderHTMLContent: React.FC<{
  html: string | undefined;
  title: string;
  isStreaming: boolean;
}> = ({ html, title, isStreaming }) => {
  const resultJson = useToolResultContent('RenderHTML');
  let pdfUrl: string | undefined;
  let pdfFileName: string | undefined;
  let htmlUrl: string | undefined;
  if (resultJson) {
    try {
      const result = JSON.parse(resultJson);
      pdfUrl = result?.pdfUrl as string | undefined;
      pdfFileName = result?.fileName as string | undefined;
      htmlUrl = result?.htmlUrl as string | undefined;
    } catch {
      // ignore
    }
  }
  return (
    <div className="flex flex-col gap-2">
      {/* Inline HTML (legacy / streaming mode) */}
      {html && (
        <HtmlRenderer html={html} title={title} isStreaming={isStreaming} />
      )}
      {/* File-based mode: render from URL once result arrives */}
      {!html && htmlUrl && !isStreaming && (
        <HtmlFromUrlRenderer url={htmlUrl} title={title} />
      )}
      {pdfUrl && !isStreaming && (
        <PdfArtifactCard
          url={pdfUrl}
          fileName={pdfFileName ?? `${title}.pdf`}
        />
      )}
    </div>
  );
};

interface MessageContentRendererProps {
  content: ContentBlock;
  role: 'user' | 'assistant';
  /** Message id (for plan progress: presentPlan registers this id, updatePlanProgress applies to it) */
  messageId?: string;
  onToolExpand?: () => void;
  isLastMessage?: boolean;
  isLastContent?: boolean;
  isLoading?: boolean;
  preventToolUseBlockToCollapse?: boolean;
  isStreaming?: boolean;
}

const MessageContentRendererInner: React.FC<MessageContentRendererProps> = ({
  content,
  role,
  messageId,
  onToolExpand,
  isLastMessage = false,
  isLastContent = false,
  isLoading = false,
  preventToolUseBlockToCollapse = false,
  isStreaming = false,
}) => {
  const { t } = useTranslation('pleri/chat');
  const hideToolUse = useChatStore(
    (state) =>
      state.conversations[state.activeConversationId ?? '']?.hideToolUse,
  );
  switch (content?.type) {
    case 'text':
      if (content?.metadata?.contentType === 'file') {
        return <FileTextContent content={content} />;
      }
      return (
        <TextContent
          content={content}
          role={role}
          isLastMessage={isLastMessage}
          isLastContent={isLastContent}
          isLoading={isLoading}
        />
      );
    case 'image':
      return <ImageContent content={content} />;
    case 'document':
      return <DocumentContent content={content} />;
    case 'image_url':
      return <ImageUrlContent content={content} />;
    case 'file':
      return <FileContent content={content} />;
    case 'toolUse': {
      if (hideToolUse) {
        return <></>;
      }

      // Normalize old tool names from existing conversations
      const toolNameCompat: Record<string, string> = {
        // Legacy cloudShell* names
        cloudShellRun: 'shellRun',
        cloudShellWriteFiles: 'writeFiles',
        cloudShellReadFile: 'readFile',
        cloudShellEditFile: 'editFile',
        cloudShellListFiles: 'listFiles',
        cloudShellGetDownloadLink: 'shareFile',
        cloudShellPrint: 'printFile',
        cloudShellGlob: 'listFiles',
        cloudShellPublish: 'shareFile',
        // workspace* → new names
        workspaceWriteFiles: 'writeFiles',
        workspaceReadFile: 'readFile',
        workspaceEditFile: 'editFile',
        workspaceListFiles: 'listFiles',
        workspaceDeleteFile: 'deleteFile',
        workspaceGetDownloadLink: 'shareFile',
        workspacePrint: 'printFile',
        // staging tool renames
        stageSkillForShell: 'copyFile',
        stageFileForShell: 'copyFile',
      };
      const toolName = toolNameCompat[content.name] ?? content.name;

      // Internal meta-tools — render as subtle activity pills, not full cards
      if (
        toolName === 'loadTools' ||
        toolName === 'loadSkill' ||
        toolName === 'getSkillResource'
      ) {
        return (
          <AgentActivityPill content={content} isStreaming={isStreaming} />
        );
      }

      if (toolName === 'GetDashboard') {
        const dashboardId = content.input?.dashboardId as string | undefined;
        return (
          <StreamingGuard
            isStreaming={isStreaming}
            isValid={() => !!dashboardId}
          >
            <UniversalDashboardRenderer
              dashboardId={dashboardId as string}
              variant="expandable"
              enableExpand
              enablePDF
              enableScreenshot
              enableScaling
              height="400px"
              isStreaming={isStreaming}
            />
          </StreamingGuard>
        );
      }

      if (toolName === 'ManageDashboard') {
        const schema = content.input?.dashboardSchema as
          | SimpleDashboardConfig
          | undefined;
        const hasSchema = schema && Object.keys(schema).length > 0;
        return (
          <StreamingGuard isStreaming={isStreaming} isValid={() => !!hasSchema}>
            <UniversalDashboardRenderer
              config={(schema || {}) as SimpleDashboardConfig}
              variant="expandable"
              enableExpand
              enablePDF
              enableScreenshot
              enableScaling
              title={t('dashboard')}
              height="350px"
              className="my-4"
              designWidth={1400}
              maxScale={0.5}
              minScale={0.25}
              isStreaming={isStreaming}
            />
          </StreamingGuard>
        );
      }

      if (toolName === 'GenerateReport') {
        const markdown = content.input?.markdown as string | undefined;
        return (
          <StreamingGuard
            isStreaming={isStreaming}
            isValid={() => !!markdown && markdown.length > 10}
          >
            <MarkdownWrapper
              content={markdown as string}
              title={t('generatedReport')}
              pdfFilename="generated-report.pdf"
            />
          </StreamingGuard>
        );
      }

      if (toolName === 'RenderHTML') {
        const htmlContent = content.input?.html as string | undefined;
        const htmlTitle =
          (content.input?.title as string | undefined) ?? t('htmlDocument');
        return (
          <ToolRendererErrorBoundary
            toolName="RenderHTML"
            fallback={
              <ToolUseContent
                content={content}
                onExpand={onToolExpand}
                preventToolUseBlockToCollapse={preventToolUseBlockToCollapse}
              />
            }
          >
            <StreamingGuard
              isStreaming={isStreaming}
              isValid={() => !!htmlContent || !!content.input?.filePath}
            >
              <RenderHTMLContent
                html={htmlContent}
                title={htmlTitle}
                isStreaming={isStreaming}
              />
            </StreamingGuard>
          </ToolRendererErrorBoundary>
        );
      }

      if (toolName === 'writeFiles') {
        return (
          <ToolRendererErrorBoundary
            toolName="writeFiles"
            fallback={
              <ToolUseContent
                content={content}
                onExpand={onToolExpand}
                preventToolUseBlockToCollapse={preventToolUseBlockToCollapse}
              />
            }
          >
            <WriteFilesBlock content={content} isStreaming={isStreaming} />
          </ToolRendererErrorBoundary>
        );
      }

      if (toolName === 'shareFile') {
        return (
          <ToolRendererErrorBoundary
            toolName="shareFile"
            fallback={
              <ToolUseContent
                content={content}
                onExpand={onToolExpand}
                preventToolUseBlockToCollapse={preventToolUseBlockToCollapse}
              />
            }
          >
            <CloudShellDownloadLinkResult content={content} isStreaming={isStreaming} />
          </ToolRendererErrorBoundary>
        );
      }

      if (toolName === 'printFile') {
        return (
          <ToolRendererErrorBoundary
            toolName="printFile"
            fallback={
              <ToolUseContent
                content={content}
                onExpand={onToolExpand}
                preventToolUseBlockToCollapse={preventToolUseBlockToCollapse}
              />
            }
          >
            <PrintResultBlock content={content} isStreaming={isStreaming} />
          </ToolRendererErrorBoundary>
        );
      }

      if (toolName === 'listFiles') {
        return (
          <ToolRendererErrorBoundary
            toolName="listFiles"
            fallback={
              <ToolUseContent
                content={content}
                onExpand={onToolExpand}
                preventToolUseBlockToCollapse={preventToolUseBlockToCollapse}
              />
            }
          >
            <ListFilesBlock content={content} />
          </ToolRendererErrorBoundary>
        );
      }

      if (toolName === 'copyFile') {
        return (
          <ToolRendererErrorBoundary
            toolName="copyFile"
            fallback={
              <ToolUseContent
                content={content}
                onExpand={onToolExpand}
                preventToolUseBlockToCollapse={preventToolUseBlockToCollapse}
              />
            }
          >
            <CopyFileBlock content={content} />
          </ToolRendererErrorBoundary>
        );
      }

      if (toolName === 'deleteFile') {
        return (
          <ToolRendererErrorBoundary
            toolName="deleteFile"
            fallback={
              <ToolUseContent
                content={content}
                onExpand={onToolExpand}
                preventToolUseBlockToCollapse={preventToolUseBlockToCollapse}
              />
            }
          >
            <DeleteFileBlock content={content} />
          </ToolRendererErrorBoundary>
        );
      }

      if (toolName === 'readFile') {
        return (
          <ToolRendererErrorBoundary
            toolName="readFile"
            fallback={
              <ToolUseContent
                content={content}
                onExpand={onToolExpand}
                preventToolUseBlockToCollapse={preventToolUseBlockToCollapse}
              />
            }
          >
            <ReadFileBlock content={content} />
          </ToolRendererErrorBoundary>
        );
      }

      if (toolName === 'editFile') {
        return (
          <ToolRendererErrorBoundary
            toolName="editFile"
            fallback={
              <ToolUseContent
                content={content}
                onExpand={onToolExpand}
                preventToolUseBlockToCollapse={preventToolUseBlockToCollapse}
              />
            }
          >
            <EditFileBlock content={content} />
          </ToolRendererErrorBoundary>
        );
      }

      if (toolName === 'shellRun') {
        return (
          <ToolRendererErrorBoundary
            toolName="shellRun"
            fallback={
              <ToolUseContent
                content={content}
                onExpand={onToolExpand}
                preventToolUseBlockToCollapse={preventToolUseBlockToCollapse}
              />
            }
          >
            <TerminalCommandBlock content={content} isStreaming={isStreaming} />
          </ToolRendererErrorBoundary>
        );
      }

      if (
        toolName === 'listPleriSkills' ||
        toolName === 'createPleriSkill' ||
        toolName === 'updatePleriSkill' ||
        toolName === 'deletePleriSkill' ||
        toolName === 'forkTemplate'
      ) {
        return (
          <ToolRendererErrorBoundary
            toolName={toolName}
            fallback={
              <ToolUseContent
                content={content}
                onExpand={onToolExpand}
                preventToolUseBlockToCollapse={preventToolUseBlockToCollapse}
              />
            }
          >
            <SkillManagementBlock content={content} />
          </ToolRendererErrorBoundary>
        );
      }

      if (toolName === 'shellAssumeRole' || toolName === 'shellListAssumableRoles') {
        return (
          <ToolRendererErrorBoundary
            toolName={toolName}
            fallback={
              <ToolUseContent
                content={content}
                onExpand={onToolExpand}
                preventToolUseBlockToCollapse={preventToolUseBlockToCollapse}
              />
            }
          >
            <ShellCredentialBlock content={content} />
          </ToolRendererErrorBoundary>
        );
      }

      if (toolName === 'askQuestion') {
        return (
          <AskQuestionContent
            content={content}
            isLastMessage={isLastMessage}
          />
        );
      }

      if (toolName === 'presentPlan') {
        return <PlanContent content={content} messageId={messageId} />;
      }

      if (toolName === 'updatePlanProgress') {
        return <UpdatePlanProgressContent content={content} />;
      }

      return (
        <ToolUseContent
          content={content}
          onExpand={onToolExpand}
          preventToolUseBlockToCollapse={preventToolUseBlockToCollapse}
        />
      );
    }
    case 'reasoning':
      return (
        <ReasoningContent
          content={content}
          isLastMessage={isLastMessage}
          isLastContent={isLastContent}
          isLoading={isLoading}
        />
      );
    case 'error':
      return <ErrorContent content={content} />;
    case 'toolResult':
      // Skip rendering tool results in the chat
      return null;
    case 'toolCallChunk': {
      // Convert toolCallChunk to toolUse-like structure and recursively render
      if (hideToolUse) {
        return <></>;
      }

      const toolUseFromChunk = convertToolCallChunkToToolUse(content);

      // Recursively call MessageContentRenderer with the converted toolUse
      // This ensures all special tool logic (GetDashboard, CreateDashboard, etc.) is applied
      return (
        <>
          <MessageContentRenderer
            content={toolUseFromChunk}
            role={role}
            messageId={messageId}
            onToolExpand={onToolExpand}
            isLastMessage={isLastMessage}
            isLastContent={isLastContent}
            isLoading={isLoading}
            preventToolUseBlockToCollapse={preventToolUseBlockToCollapse}
            isStreaming={isLoading}
          />
        </>
      );
    }
    default:
      return null;
  }
};

export const MessageContentRenderer = MessageContentRendererInner;
